package fr.manyverse.app.presentation.viewmodel

import androidx.lifecycle.viewModelScope
import fr.manyverse.app.domain.model.ActiveAnnouncement
import fr.manyverse.app.domain.model.AlertType
import fr.manyverse.app.domain.model.DieConfig
import fr.manyverse.app.domain.model.DieType
import fr.manyverse.app.domain.model.EventContent
import fr.manyverse.app.domain.model.EventType
import fr.manyverse.app.domain.model.GameEvent
import fr.manyverse.app.domain.usecase.CreateDiceRollEventUseCase
import fr.manyverse.app.domain.usecase.GetTableEventsUseCase
import fr.manyverse.app.domain.usecase.LeaveTableUseCase
import fr.manyverse.app.domain.usecase.ObserveRealtimeReconnectionsUseCase
import fr.manyverse.app.domain.usecase.ObserveTableEventsUseCase
import fr.manyverse.app.domain.usecase.SendMessageUseCase
import fr.manyverse.app.domain.usecase.SendPlayerAlertUseCase
import fr.manyverse.app.infrastructure.audio.CrossfadeAudioPlayer
import fr.manyverse.app.infrastructure.datastore.StoredDiceConfig
import fr.manyverse.app.infrastructure.datastore.UserPreferencesDataStore
import fr.manyverse.app.presentation.components.common_tabs.conversationKey
import fr.manyverse.app.presentation.components.notification.NotificationContent
import fr.manyverse.app.presentation.error.UserErrorNotifier
import fr.manyverse.app.presentation.state.AsyncUiState
import kotlin.time.Clock
import kotlin.time.Instant
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

private const val INITIAL_MESSAGES_LIMIT = 500

class PlayerViewModel(
    private val tableId: String,
    private val currentUserId: String,
    private val availableDice: List<DieConfig>,
    private val createDiceRollEventUseCase: CreateDiceRollEventUseCase,
    private val getTableEventsUseCase: GetTableEventsUseCase,
    private val observeTableEventsUseCase: ObserveTableEventsUseCase,
    private val sendMessageUseCase: SendMessageUseCase,
    private val sendPlayerAlertUseCase: SendPlayerAlertUseCase,
    private val leaveTableUseCase: LeaveTableUseCase,
    observeRealtimeReconnectionsUseCase: ObserveRealtimeReconnectionsUseCase,
    private val audioPlayer: CrossfadeAudioPlayer,
    private val userPreferencesDataStore: UserPreferencesDataStore,
    private val errorNotifier: UserErrorNotifier
) : BaseViewModel() {

    /** Retours du temps réel après une coupure : l'écran y relance la même resynchronisation qu'au retour au premier plan. */
    val reconnections: Flow<Unit> = observeRealtimeReconnectionsUseCase()

    private val _savedDiceConfig = MutableStateFlow<AsyncUiState<StoredDiceConfig?>>(AsyncUiState.Loading)
    val savedDiceConfig: StateFlow<AsyncUiState<StoredDiceConfig?>> = _savedDiceConfig.asStateFlow()

    private val _activeAnnouncements = MutableStateFlow<List<ActiveAnnouncement>>(emptyList())
    val activeAnnouncements: StateFlow<List<ActiveAnnouncement>> = _activeAnnouncements.asStateFlow()

    private val _minimizedAnnouncementIds = MutableStateFlow<Set<String>>(emptySet())
    val minimizedAnnouncementIds: StateFlow<Set<String>> = _minimizedAnnouncementIds.asStateFlow()

    private val _notifications = MutableStateFlow<List<NotificationContent>>(emptyList())
    val notifications: StateFlow<List<NotificationContent>> = _notifications.asStateFlow()

    private val _diceRollState = MutableStateFlow<AsyncUiState<Unit>>(AsyncUiState.Idle)
    val diceRollState: StateFlow<AsyncUiState<Unit>> = _diceRollState.asStateFlow()

    private val _messages = MutableStateFlow<List<GameEvent>>(emptyList())
    val messages: StateFlow<List<GameEvent>> = _messages.asStateFlow()

    // Initialisé au moment de la création de la VM : l'historique chargé est considéré comme déjà lu,
    // seuls les messages reçus via Realtime après ce point compteront comme non lus.
    private val _lastReadMessageAt = MutableStateFlow<Instant>(Clock.System.now())
    val unreadMessageCount: StateFlow<Int> = combine(_messages, _lastReadMessageAt) { messages, lastReadAt ->
        messages.count { it.createdAt > lastReadAt && it.triggeredBy != currentUserId }
    }.stateIn(viewModelScope, SharingStarted.Eagerly, 0)

    private val _isKicked = MutableStateFlow(false)
    val isKicked: StateFlow<Boolean> = _isKicked.asStateFlow()

    private val _isLeft = MutableStateFlow(false)
    val isLeft: StateFlow<Boolean> = _isLeft.asStateFlow()

    private val shownAnnouncementIds = mutableSetOf<String>()
    private var latestEventAt: Instant? = null

    init {
        loadInitialData()
        startObservingEvents()
        viewModelScope.launch {
            _savedDiceConfig.value =
                AsyncUiState.Success(userPreferencesDataStore.getDiceConfig(currentUserId, tableId))
        }
    }

    fun saveDiceConfig(dieType: DieType, count: Int, modifier: Int) {
        // Mise à jour immédiate de l'état : l'onglet dés est détruit dès qu'on le quitte et se
        // reconstruit à partir de ce flow. Sans ça, il repartirait de la config chargée à l'init.
        _savedDiceConfig.value = AsyncUiState.Success(StoredDiceConfig(dieType.name, count, modifier))
        viewModelScope.launch {
            userPreferencesDataStore.saveDiceConfig(currentUserId, tableId, dieType.name, count, modifier)
        }
    }

    private fun loadInitialData() {
        viewModelScope.launch {
            val since = latestEventAt
            getTableEventsUseCase(
                tableId, limit = INITIAL_MESSAGES_LIMIT, offset = 0,
                eventTypes = listOf(EventType.MESSAGE_SENT, EventType.ANNOUNCEMENT, EventType.ANNOUNCEMENT_CANCELLED),
                since = since
            ).onSuccess { events ->
                if (events.isEmpty()) return@onSuccess
                latestEventAt = events.maxOf { it.createdAt }

                val newMessages = events
                    .filter { it.eventType == EventType.MESSAGE_SENT }
                    .filter { currentUserId in it.targetParticipants || it.triggeredBy == currentUserId }
                if (newMessages.isNotEmpty()) {
                    _messages.update { current ->
                        if (since == null) {
                            newMessages.sortedByDescending { it.createdAt }
                        } else {
                            val existingIds = current.mapTo(HashSet()) { it.id }
                            val incoming = newMessages.filter { it.id !in existingIds }
                            (incoming + current).sortedByDescending { it.createdAt }
                        }
                    }
                }

                val cancelledIds = events
                    .filter { it.eventType == EventType.ANNOUNCEMENT_CANCELLED }
                    .mapNotNull { (it.content as? EventContent.AnnouncementCancelled)?.announcementEventId }
                    .toSet()

                if (cancelledIds.isNotEmpty()) {
                    _activeAnnouncements.update { current -> current.filter { it.eventId !in cancelledIds } }
                    _minimizedAnnouncementIds.update { current -> current - cancelledIds }
                }

                val existingIds = _activeAnnouncements.value.map { it.eventId }.toSet()
                val toShow = events
                    .filter {
                        it.eventType == EventType.ANNOUNCEMENT &&
                        currentUserId in it.targetParticipants &&
                        it.id !in cancelledIds &&
                        it.id !in shownAnnouncementIds &&
                        it.id !in existingIds
                    }
                    .sortedBy { it.createdAt }

                toShow.forEach { event ->
                    val content = event.content as? EventContent.Announcement ?: return@forEach
                    shownAnnouncementIds.add(event.id)
                    _activeAnnouncements.update {
                        it + ActiveAnnouncement(
                            eventId = event.id,
                            text = content.text,
                            imageUrl = content.imageUrl,
                            targetParticipantIds = event.targetParticipants,
                            createdAt = event.createdAt
                        )
                    }
                }
                if (toShow.isNotEmpty()) {
                    _minimizedAnnouncementIds.update { current ->
                        current + toShow.map { it.id }.toSet()
                    }
                }
            }
        }
    }

    private fun startObservingEvents() {
        viewModelScope.launch {
            observeTableEventsUseCase(tableId).collect { event ->
                val current = latestEventAt
                if (current == null || event.createdAt > current) {
                    latestEventAt = event.createdAt
                }
                when (event.eventType) {
                    EventType.DICE_ROLLED -> {
                        if (event.triggeredBy != currentUserId) {
                            val content = event.content as? EventContent.DiceRolled ?: return@collect
                            if (content.isGmRoll) return@collect
                            val dieConfig = availableDice.find { it.type.name == content.dieType }
                            _notifications.update {
                                it + NotificationContent.DiceRoll(
                                    id = event.id,
                                    characterName = content.characterName,
                                    dieType = content.dieType,
                                    count = content.count,
                                    results = content.results,
                                    criticalMin = dieConfig?.criticalMin,
                                    criticalMax = dieConfig?.criticalMax,
                                    modifier = content.modifier,
                                    highValuesWin = dieConfig?.highValuesWin ?: true
                                )
                            }
                        }
                    }
                    EventType.MESSAGE_SENT -> {
                        val isRecipient = currentUserId in event.targetParticipants
                        val isSender = event.triggeredBy == currentUserId
                        if (isRecipient || isSender) {
                            _messages.update { listOf(event) + it }
                        }
                        if (isRecipient && !isSender) {
                            val content = event.content as? EventContent.MessageSent ?: return@collect
                            _notifications.update {
                                it + NotificationContent.NewMessage(
                                    id = event.id,
                                    senderName = content.senderName,
                                    text = content.text,
                                    conversationKey = event.conversationKey()
                                )
                            }
                        }
                    }
                    EventType.PLAYER_JOINED -> {
                        if (event.triggeredBy != currentUserId) {
                            val content = event.content as? EventContent.PlayerJoined ?: return@collect
                            _notifications.update {
                                it + NotificationContent.PlayerJoined(
                                    id = event.id,
                                    characterName = content.characterName
                                )
                            }
                        }
                    }
                    EventType.ITEM_SENT -> {
                        if (currentUserId in event.targetParticipants && event.triggeredBy != currentUserId) {
                            val content = event.content as? EventContent.ItemSent ?: return@collect
                            _notifications.update {
                                it + NotificationContent.ItemReceived(
                                    id = event.id,
                                    senderName = content.senderName
                                )
                            }
                        }
                    }
                    EventType.ANNOUNCEMENT -> {
                        if (currentUserId !in event.targetParticipants) return@collect
                        if (event.id in shownAnnouncementIds) return@collect
                        val content = event.content as? EventContent.Announcement ?: return@collect
                        shownAnnouncementIds.add(event.id)
                        _minimizedAnnouncementIds.update { current ->
                            current + _activeAnnouncements.value.map { it.eventId }.toSet()
                        }
                        _activeAnnouncements.update {
                            it + ActiveAnnouncement(
                                eventId = event.id,
                                text = content.text,
                                imageUrl = content.imageUrl,
                                targetParticipantIds = event.targetParticipants,
                                createdAt = event.createdAt
                            )
                        }
                    }
                    EventType.PLAYER_KICKED -> {
                        if (currentUserId in event.targetParticipants) {
                            _isKicked.value = true
                        }
                    }
                    EventType.ANNOUNCEMENT_CANCELLED -> {
                        val content = event.content as? EventContent.AnnouncementCancelled
                        val cancelledId = content?.announcementEventId
                        if (cancelledId != null) {
                            _activeAnnouncements.update { it.filter { a -> a.eventId != cancelledId } }
                            _minimizedAnnouncementIds.update { it - cancelledId }
                        } else {
                            _activeAnnouncements.value = emptyList()
                            _minimizedAnnouncementIds.value = emptySet()
                        }
                    }
                    else -> Unit
                }
            }
        }
    }

    fun createDiceRollEvent(
        characterName: String,
        dieType: DieType,
        count: Int,
        results: List<Int>,
        modifier: Int = 0
    ) {
        viewModelScope.launch {
            createDiceRollEventUseCase(
                tableId = tableId,
                triggeredBy = currentUserId,
                characterName = characterName,
                dieType = dieType,
                count = count,
                results = results,
                modifier = modifier
            ).onFailure(errorNotifier::notify)
        }
    }

    fun playEffect(path: String, soundId: String) {
        audioPlayer.playEffect(path, soundId)
    }

    fun refreshOnForeground() {
        loadInitialData()
    }

    fun minimizeAnnouncement(announcementEventId: String) {
        _minimizedAnnouncementIds.update { it + announcementEventId }
    }

    fun restoreAnnouncement(announcementEventId: String) {
        _minimizedAnnouncementIds.update { current ->
            val allIds = _activeAnnouncements.value.map { it.eventId }.toSet()
            (current + allIds) - announcementEventId
        }
    }

    fun dismissAnnouncement(announcementEventId: String) {
        _activeAnnouncements.update { it.filter { a -> a.eventId != announcementEventId } }
        _minimizedAnnouncementIds.update { it - announcementEventId }
    }

    fun sendMessage(text: String, recipientIds: List<String>, senderName: String) {
        viewModelScope.launch {
            sendMessageUseCase(
                tableId = tableId,
                senderId = currentUserId,
                senderName = senderName,
                recipientIds = recipientIds,
                text = text
            ).onFailure(errorNotifier::notify)
        }
    }

    fun sendAlert(gmId: String, characterName: String, alertType: AlertType, message: String?) {
        viewModelScope.launch {
            sendPlayerAlertUseCase(
                tableId = tableId,
                playerId = currentUserId,
                gmId = gmId,
                characterName = characterName,
                alertType = alertType,
                message = message
            ).onFailure(errorNotifier::notify)
        }
    }

    fun clearUnreadMessages() {
        val latest = _messages.value.maxOfOrNull { it.createdAt } ?: return
        if (latest > _lastReadMessageAt.value) {
            _lastReadMessageAt.value = latest
        }
    }

    fun dismissNotification(id: String) {
        _notifications.update { it.filter { n -> n.id != id } }
    }

    fun leaveTable() {
        viewModelScope.launch {
            leaveTableUseCase(tableId = tableId, userId = currentUserId)
                .onSuccess { _isLeft.value = true }
                .onFailure(errorNotifier::notify)
        }
    }
}
